import { useTranslation } from 'react-i18next';
import type { Team } from '../../../api/projects-client/projects-client.type';
import { ContentPlaceholder } from '../../../components/content-placeholder/content-placeholder';
import { Table } from '../../../components/table/table';
import { formatCurrency, getCurrency } from '../../../helpers/currency-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { Text } from '../../../ui-library/typography/typography';
import { TableColumn } from '../../data-management/components/table';
import { getEpicsTableColumns } from '../epic-list/epics-main-section.helpers';
import type { StrategyEntity, StrategyEntityType } from '../strategy.type';
import { EntityCompletionCell, EntityEndDateCell, EntityIDCell } from './strategy-entity-cells';
import { getEntityTeamNames, sortStrategyEntities } from './strategy-entity-table.helpers';

type EntityType = 'initiative' | 'goal' | 'outcome' | 'epic';

type Props<T extends StrategyEntity> = {
  entities: T[];
  entityType: EntityType;
  onEntityClick: (entity: T) => void;
  selectedEntityId?: string;
  page: number;
  onPageChange: (page: number) => void;
  tableSortBy: keyof T | string;
  tableSortDirection: 'asc' | 'desc';
  onSortChange?: (sortBy: string, direction: 'asc' | 'desc') => void;
  showTeamsColumn?: boolean;
  teams?: Team[];
  filtersApplied?: boolean;
};

/**
 * Unified table component for displaying strategy entities (Initiatives, Goals, Outcomes)
 */
export function StrategyEntityTable<T extends StrategyEntity>({
  entities,
  entityType,
  onEntityClick,
  selectedEntityId,
  page,
  onPageChange,
  tableSortBy,
  tableSortDirection,
  onSortChange,
  showTeamsColumn = true,
  teams = [],
  filtersApplied = true,
}: Props<T>) {
  const i18n = useTranslation();
  const organization = useGlobalStore((state) => state.organization);
  const { code: currencyCode } = getCurrency(organization);
  // Get entity-specific labels
  const getLabel = (key: string): string => {
    const labelMap: Record<EntityType, Record<string, string>> = {
      initiative: {
        entity_id: i18n.t('strategy.initiatives.table.initiative_id'),
        name: i18n.t('strategy.initiatives.table.initiative_name'),
        completion: i18n.t('strategy.initiatives.table.completion'),
        target_end: i18n.t('strategy.initiatives.table.target_end'),
        cost_to_date: i18n.t('strategy.initiatives.table.cost_to_date'),
        teams: i18n.t('strategy.initiatives.table.teams'),
      },
      goal: {
        entity_id: `${i18n.t('strategy.initiatives.table.strategic_goal_id')}`,
        name: `${i18n.t('strategy.initiatives.table.strategic_goal_name')}`,
        completion: i18n.t('strategy.initiatives.table.completion'),
        target_end: i18n.t('strategy.initiatives.table.target_end'),
        cost_to_date: i18n.t('strategy.initiatives.table.cost_to_date'),
        teams: i18n.t('strategy.initiatives.table.teams'),
      },
      outcome: {
        entity_id: i18n.t('strategy.initiatives.table.outcome_id'),
        name: `${i18n.t('strategy.initiatives.table.outcome_name')}`,
        completion: i18n.t('strategy.initiatives.table.completion'),
        target_end: i18n.t('strategy.initiatives.table.target_end'),
        cost_to_date: i18n.t('strategy.initiatives.table.cost_to_date'),
        teams: i18n.t('strategy.initiatives.table.teams'),
      },
      epic: {
        entity_id: i18n.t('strategy.initiatives.table.epic_id'),
        name: i18n.t('strategy.initiatives.table.epic_name'),
        completion: i18n.t('strategy.initiatives.table.completion'),
        target_end: i18n.t('strategy.initiatives.table.target_end'),
        cost_to_date: i18n.t('strategy.initiatives.table.cost_to_date'),
        teams: i18n.t('strategy.initiatives.table.teams'),
      },
    };

    return labelMap[entityType][key] || key;
  };

  // Define table columns
  const getTableColumns = (): TableColumn<T>[] => {
    // For epics, use the shared column configuration from epics-main-section.helpers
    if (entityType === 'epic') {
      return getEpicsTableColumns(i18n.t).map((col) => ({
        ...col,
        key: col.key as keyof T,
      })) as TableColumn<T>[];
    }

    // Standard columns for Initiative, Goal, Outcome
    const columns: TableColumn<T>[] = [
      {
        key: 'key' as keyof T,
        label: getLabel('entity_id'),
        width: '10%',
        sortable: true,
        render: (_, entity) => <EntityIDCell entity={entity} entityType={entityType as StrategyEntityType} />,
      },
      {
        key: 'name' as keyof T,
        label: getLabel('name'),
        width: '40%',
        sortable: true,
        render: (_, entity) => <Text size="small">{entity.name}</Text>,
      },
      {
        key: 'percent_progress' as keyof T,
        label: getLabel('completion'),
        width: '10%',
        sortable: true,
        render: (_, entity) => <EntityCompletionCell entity={entity} />,
      },
      {
        key: 'end_date' as keyof T,
        label: getLabel('target_end'),
        width: '10%',
        sortable: true,
        render: (_, entity) => <EntityEndDateCell entity={entity} />,
      },
      {
        key: 'costs_to_date' as keyof T,
        label: getLabel('cost_to_date'),
        width: '10%',
        sortable: true,
        render: (_, entity) => {
          const cost = (entity as any).costs_to_date?.initiative ?? 0;
          const formattedCost = formatCurrency(cost, { currency: currencyCode });

          return <Text size="small">{formattedCost}</Text>;
        },
      },
    ];

    // Add teams column if requested
    if (showTeamsColumn) {
      columns.push({
        // Keyed to the Initiatives column's field name on purpose: `tableSortBy` is one
        // persisted value shared by every Strategy tab, and the Initiatives table writes
        // it back from inside its own sort. A key that only exists here would be clobbered
        // on the next tab switch.
        key: 'contributing_teams_label' as keyof T,
        label: getLabel('teams'),
        width: '10%',
        sortable: true,
        render: (_, entity) => {
          const teamNames = getEntityTeamNames(entity, teams);
          return <Text size="small">{teamNames.length > 0 ? teamNames.join(', ') : '-'}</Text>;
        },
      });
    }

    return columns;
  };

  // Custom sort handler
  const handleSort = (data: T[], sortBy: keyof T, direction: 'asc' | 'desc'): T[] => {
    if (onSortChange) {
      onSortChange(sortBy as string, direction);
    }
    return sortStrategyEntities(data, sortBy as string, direction, teams) as T[];
  };

  // Show placeholder if no filters applied
  if (!filtersApplied) {
    return <ContentPlaceholder noFiltersApplied />;
  }

  // Show placeholder if no entities
  if (entities.length === 0) {
    return <ContentPlaceholder />;
  }

  return (
    <Table<T>
      data={entities}
      columns={getTableColumns()}
      getRowId={(entity) => entity.id}
      defaultSortedBy={tableSortBy as keyof T}
      defaultSortedByDirection={tableSortDirection}
      stickyHeader
      onRowClick={onEntityClick}
      currentPage={page}
      totalItems={entities.length}
      onPageChange={onPageChange}
      customSort={handleSort}
      selectedRowId={selectedEntityId}
    />
  );
}
